import { useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { FeaturedFilms } from "@/components/FeaturedFilms";
import { PORTFOLIO } from "@/data/content";
import { Play } from "lucide-react";

const VideoCard = ({ item }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  const onEnter = () => {
    const v = ref.current;
    if (v) {
      v.play().then(() => setPlaying(true)).catch(() => {});
    }
  };
  const onLeave = () => {
    const v = ref.current;
    if (v) {
      v.pause();
      v.currentTime = 0;
      setPlaying(false);
    }
  };

  if (item.placeholder) {
    return (
      <Reveal className="group">
        <div
          data-testid={`portfolio-item-${item.id}`}
          className="relative overflow-hidden border border-border bg-secondary"
        >
          <div className="aspect-[16/9] w-full flex flex-col items-center justify-center text-center px-6">
            <p className="overline mb-3">{item.type}</p>
            <h3 className="font-display font-light text-3xl sm:text-4xl text-foreground/40 leading-none">
              {item.title}
            </h3>
            <p className="mt-3 text-muted-foreground text-sm tracking-wide">
              {item.location}
            </p>
          </div>
        </div>
      </Reveal>
    );
  }

  return (
    <Reveal className="group">
      <div
        data-testid={`portfolio-item-${item.id}`}
        className="relative overflow-hidden cursor-pointer film-grain"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <div className="aspect-[16/9] w-full overflow-hidden bg-secondary">
          <video
            ref={ref}
            muted
            loop
            playsInline
            preload="metadata"
            poster={item.poster}
            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          >
            <source src={item.video} type="video/mp4" />
          </video>
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />

        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
            playing ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="w-16 h-16 rounded-full border border-[#F7F5F0]/60 flex items-center justify-center text-[#F7F5F0] backdrop-blur-sm">
            <Play strokeWidth={1} className="ml-0.5" />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-7 sm:p-9 z-[2]">
          <p className="overline text-[#C9BCA8] mb-2">{item.type}</p>
          <h3 className="font-display font-light text-3xl sm:text-4xl text-[#F7F5F0] leading-none">
            {item.title}
          </h3>
          <p className="mt-2 text-[#F7F5F0]/70 text-sm tracking-wide">
            {item.location}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

export const Portfolio = () => {
  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="bg-background py-24 sm:py-32 lg:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-16 sm:mb-24">
          <div>
            <Reveal>
              <p className="overline mb-5">Selected Work</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground leading-none">
                The Film Reel
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-muted-foreground leading-relaxed text-sm sm:text-base">
              Each film was composed entirely from existing
              photography - no additional shoot.
            </p>
          </Reveal>
        </div>

        <FeaturedFilms />

        <div className="hidden md:grid grid-cols-2 gap-5 sm:gap-7">
          {PORTFOLIO.map((item) => (
            <VideoCard key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
